import React, { useContext } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ChevronRight, CheckCircle2, Sparkles } from "lucide-react";
import PageHero from "@/components/PageHero";
import { SERVICES, localiseService, GALLERY } from "@/lib/data";
import { BookContext } from "@/components/Layout";
import { useLang } from "@/lib/i18n";

const iconFor = (name) => Icons[name] || Icons.Stethoscope;

// Curated facility imagery in a modern bento layout
const FACILITY_BENTO = [
  { src: "https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/mri-magnetom-essenza-dot-product-image-sh_mr_3.TIF/:/rs=w:1300,h:800", caption: "1.5 Tesla MRI Suite", span: "lg:col-span-7 lg:row-span-2" },
  { src: "https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/Small%20-%2072%20DPI-SH_CT_42161_16.TIF.jpg/:/rs=w:900,h:700", caption: "16-Slice CT Scanner", span: "lg:col-span-5" },
  { src: "https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/OPD1-ac36ba4.png/:/rs=w:900,h:700", caption: "Multi-Specialty OPD", span: "lg:col-span-5" },
  { src: "https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/1Img.webp/:/rs=w:900,h:700", caption: "Reception & Lounge", span: "lg:col-span-4" },
  { src: "https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/3img.png/:/rs=w:900,h:700", caption: "Diagnostic Floor", span: "lg:col-span-4" },
  { src: "https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/img4.jpeg/:/rs=w:900,h:700", caption: "Procedure Suite", span: "lg:col-span-4" },
  { src: "https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/10img.png/:/rs=w:900,h:700", caption: "Pathology Lab", span: "lg:col-span-6" },
  { src: "https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/9img.png/:/rs=w:900,h:700", caption: "Patient Care", span: "lg:col-span-6" },
  { src: "https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/7img.png/:/rs=w:900,h:700", caption: "Imaging Workflow", span: "lg:col-span-4" },
  { src: "https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/8img.png/:/rs=w:900,h:700", caption: "Day-Care Ward", span: "lg:col-span-4" },
  { src: "https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/Small%20-%2072%20DPI-SH_CT_42155_16.TIF.jpg/:/rs=w:900,h:700", caption: "Cross-Sectional Imaging", span: "lg:col-span-4" },
];

export default function Services() {
  const { open } = useContext(BookContext);
  const { t, lang } = useLang();

  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow={t("services.eyebrow")}
        title={t("services.title")}
        subtitle={t("services.subtitle")}
        crumbs={[{ label: t("nav.facility") }]}
        image="https://images.unsplash.com/photo-1666214280352-db292c05fd80?w=1600&q=80"
      />

      {/* Services grid */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((raw) => {
            const s = localiseService(raw, lang);
            const Icon = iconFor(s.icon);
            return (
              <div key={s.slug} className="group bg-white rounded-3xl overflow-hidden border border-black/5 shadow-soft hover:shadow-lifted hover:-translate-y-1 transition-all" data-testid={`svc-${s.slug}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center">
                      <Icon className="w-5 h-5" strokeWidth={1.7} />
                    </div>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-gold font-bold">{s.short}</span>
                  </div>
                  <h3 className="font-display text-2xl text-deep font-semibold mb-2">{s.name}</h3>
                  <p className="text-sm text-ink-soft mb-5">{s.blurb}</p>
                  <ul className="space-y-1.5 mb-6">
                    {s.details.slice(0, 3).map((d) => (
                      <li key={d} className="text-xs text-ink-soft flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-brand shrink-0" strokeWidth={2} /> {d}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between">
                    <Link to={`/services/${s.slug}`} className="text-sm font-semibold text-brand inline-flex items-center gap-1 hover:gap-2 transition-all">
                      {t("services.details")} <ChevronRight className="w-4 h-4" />
                    </Link>
                    <button onClick={() => open(s.name)} className="text-sm px-4 py-2 bg-deep text-white rounded-full font-medium hover:bg-deep-700 transition-colors" data-testid={`book-${s.slug}`}>
                      {t("services.book")}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* === MODERN FACILITY GALLERY (BENTO) === */}
      <section className="py-20 sm:py-28 bg-deep text-white relative overflow-hidden" data-testid="facility-gallery">
        <div className="aurora-bg pointer-events-none" />
        <div className="absolute inset-0 subtle-grain opacity-30" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 mb-14 items-end">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-5">
                <Sparkles className="w-3.5 h-3.5 text-gold" strokeWidth={2} />
                <span className="text-[10px] tracking-[0.25em] uppercase text-gold font-bold">{t("services.galleryEyebrow")}</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-[3.4rem] font-semibold leading-[1.05] tracking-tight">
                {t("services.galleryTitle")} <span className="italic shimmer-text">— in pictures.</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-white/65 leading-relaxed">{t("services.gallerySubtitle")}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-12 auto-rows-[180px] sm:auto-rows-[220px] gap-3 sm:gap-4">
            {FACILITY_BENTO.map((g, i) => (
              <div
                key={i}
                className={`group relative overflow-hidden rounded-3xl ${g.span || "col-span-1 lg:col-span-4"}`}
                data-testid={`facility-img-${i}`}
              >
                <img
                  src={g.src}
                  alt={g.caption}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-deep/85 via-deep/0 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 inset-x-0 p-5 sm:p-6">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-gold font-bold mb-1">Inside SAT</div>
                  <div className="font-display text-lg sm:text-xl text-white font-semibold leading-tight">{g.caption}</div>
                </div>
                <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 border border-white/20 backdrop-blur flex items-center justify-center text-white/70 group-hover:bg-gold group-hover:text-deep transition-colors">
                  <Sparkles className="w-4 h-4" strokeWidth={2} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
